<script lang="ts">
	import { untrack, type Component, type Snippet } from 'svelte';
	import Checkbox from '@astlai/common/Checkbox.svelte';
	import {
		getSelectGroupContext,
		getSelectInteractiveContext,
		getSelectRegistryContext,
		type SelectValue
	} from './select-context';
	import { optionDomId } from './select-logic';

	interface Props {
		value: SelectValue;
		label: string;
		disabled?: boolean;
		icon?: Component;
		description?: string;
		children?: Snippet;
	}
	let { value, label, disabled = false, icon: Icon, description, children }: Props = $props();

	const registryCtx = getSelectRegistryContext();
	const interactiveCtx = getSelectInteractiveContext();
	const group = getSelectGroupContext();

	$effect(() => {
		const meta = { value, label, disabled, description, group };
		untrack(() => registryCtx?.register(meta));
		return () => untrack(() => registryCtx?.unregister(value));
	});

	const domId = $derived(interactiveCtx ? optionDomId(interactiveCtx.idPrefix, value) : undefined);
	const selected = $derived(interactiveCtx?.isSelected(value) ?? false);
	const highlighted = $derived(interactiveCtx?.isHighlighted(value) ?? false);
</script>

<div
	class="select-option"
	class:disabled
	class:selected
	class:highlighted
	role={interactiveCtx ? 'option' : undefined}
	id={domId}
	aria-selected={interactiveCtx ? selected : undefined}
	aria-disabled={disabled || undefined}
	onclick={() => {
		if (interactiveCtx && !disabled) {
			interactiveCtx.select(value);
		}
	}}
	onpointerenter={() => {
		if (interactiveCtx && !disabled) {
			interactiveCtx.setHighlighted(value);
		}
	}}
>
	{#if interactiveCtx?.multiple}
		<Checkbox checked={selected} />
	{/if}
	{#if Icon}
		<div class="option-icon"><Icon /></div>
	{/if}
	<div class="option-text">
		{#if children}
			{@render children()}
		{:else}
			<span class="option-label">{label}</span>
			{#if description}
				<span class="option-description">{description}</span>
			{/if}
		{/if}
	</div>
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.select-option {
		@include lib.flex-row();
		@include lib.flex-align(flex-start, center);
		gap: 0.5rem;
		padding: 0.5rem 0.75rem;
		border-radius: 0.5rem;
		cursor: pointer;
		color: var(--text-primary);
		font-size: 0.875rem;
		line-height: 0.875rem;

		&:hover,
		&.highlighted,
		&.selected {
			background: var(--state-hover);
		}

		&.disabled {
			cursor: default;
			pointer-events: none;
			opacity: 0.5;
		}

		.option-icon {
			@include lib.flex-row();
			@include lib.flex-align();
			flex-shrink: 0;
			font-size: 1rem;
			color: var(--text-tertiary);
		}

		.option-text {
			@include lib.flex-column();
			flex: 1 1 auto;
			gap: 0.125rem;
			min-width: 0;

			.option-label {
				white-space: normal;
				overflow-wrap: break-word;
			}

			.option-description {
				font-size: 0.75rem;
				color: var(--text-tertiary);
			}
		}
	}
</style>
